import { useEffect } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, type Me } from './lib/api'
import { Shell } from './shell/Shell'
import { LoginPage } from './pages/Login'
import { TodayPage } from './pages/Today'
import { CustomersPage } from './pages/Customers'
import { CustomerDetailPage } from './pages/CustomerDetail'
import { CasesPage } from './pages/Cases'
import { CaseDetailPage } from './pages/CaseDetail'
import { CalendarPage } from './pages/Calendar'
import { FinancePage } from './pages/Finance'
import { RemindersPage } from './pages/Reminders'
import { SettingsPage } from './pages/Settings'
import { Skeleton } from './components/ui'

export default function App() {
  const location = useLocation()
  const navigate = useNavigate()

  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<Me>('/api/auth/me'),
    retry: false,
    staleTime: 5 * 60_000,
  })

  const unauthenticated = me.isError && (me.error as { status?: number })?.status === 401

  useEffect(() => {
    if (unauthenticated && location.pathname !== '/login') {
      navigate('/login', { replace: true, state: { from: location.pathname } })
    }
  }, [unauthenticated, location.pathname, navigate])

  if (location.pathname === '/login') {
    return <LoginPage onDone={() => me.refetch()} />
  }

  if (me.isLoading) {
    return (
      <div className="flex min-h-dvh flex-col gap-4 p-8">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full max-w-3xl" />
        <Skeleton className="h-40 w-full max-w-3xl" />
      </div>
    )
  }

  if (!me.data) return null

  return (
    <Shell me={me.data}>
      <Routes>
        <Route path="/" element={<TodayPage me={me.data} />} />
        <Route path="/customers" element={<CustomersPage me={me.data} />} />
        <Route path="/customers/:id" element={<CustomerDetailPage me={me.data} />} />
        <Route path="/cases" element={<CasesPage me={me.data} />} />
        <Route path="/cases/:id" element={<CaseDetailPage me={me.data} />} />
        <Route path="/calendar" element={<CalendarPage me={me.data} />} />
        <Route path="/finance" element={<FinancePage me={me.data} />} />
        <Route path="/reminders" element={<RemindersPage me={me.data} />} />
        <Route path="/settings" element={<SettingsPage me={me.data} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
  )
}
